import { ResponseCode } from "@/constants";
import {
  ChangeType,
  getBalanceService,
  getVerCodeService,
  OrderInfo,
  PayStatus,
  PayType,
  requestPayService,
} from "@/services/order";
import { useRequest, useThrottleFn } from "ahooks";
import { Button, Form, Input, message, Modal } from "antd";
import { useEffect, useRef, useState } from "react";
import PaySuccessModal from "../PaySuccessModal";
import { getUserPhoneAndEmailSerivce } from "@/services/user";

type BalancePayModalProps = {
  open: boolean;
  onClose: () => void;
  orderInfo?: OrderInfo;
  onSuccess: () => void;
  onSuccessPay?: () => void;
};
export default function BalancePayModal(props: BalancePayModalProps) {
  const { open, onClose, orderInfo, onSuccess } = props;
  const [successModalOpen, setSuccessModalOpen] = useState(false);

  const { data: balance } = useRequest(
    async () => {
      if (!open) return;
      const rsp = await getBalanceService();
      if (rsp.code !== ResponseCode.Success) {
        message.error(rsp.msg);
      }
      return rsp.data;
    },
    { refreshDeps: [open] }
  );

  const [countDown, setCountDown] = useState(0);
  const timerId = useRef<NodeJS.Timeout>();
  const [hadSent, setHadSent] = useState(false);
  useEffect(() => {
    return () => {
      clearInterval(timerId.current);
    };
  }, []);

  const { run: getCode } = useThrottleFn(
    async () => {
      if (countDown > 0) return;
      setCountDown(60);
      setHadSent(true);
      timerId.current = setInterval(() => {
        setCountDown((v) => {
          if (v <= 1) {
            clearInterval(timerId.current);
            return 0;
          }
          return v - 1;
        });
      }, 1000);
      const rsp = await getVerCodeService({
        businessId: orderInfo!.orderId,
        changeType: ChangeType.CONSUME,
      });
      if (rsp.code !== ResponseCode.Success) {
        message.error(rsp.msg);
        return;
      }
    },
    { wait: 500 }
  );

  const { run: handleSubmit } = useRequest(
    async (values: { verCode: string }) => {
      const rsp = await requestPayService({
        verCode: values.verCode,
        orderId: orderInfo?.orderId,
        payType: PayType.BALANCE,
      });
      if (rsp.code !== ResponseCode.Success) {
        message.error(rsp.msg);
        return;
      }
      if (rsp.data.payStatus === PayStatus.DONE) {
        props.onSuccessPay?.();
        setSuccessModalOpen(true);
      } else {
        message.error(rsp.msg || "支付失败");
      }
    },
    { manual: true, throttleWait: 500 }
  );

  const { data: phoneOrEmail } = useRequest(async () => {
    const rsp = await getUserPhoneAndEmailSerivce();
    if (rsp.code !== ResponseCode.Success) {
      message.error(rsp.msg);
    }
    return rsp.data;
  });

  if (!orderInfo) return null;

  return (
    <>
      <Modal
        open={open}
        title="账户余额支付"
        footer={null}
        onCancel={onClose}
        maskClosable={false}
        width={480}
      >
        <Form onFinish={handleSubmit}>
          <div className="mb-[18px] text-text-regular">
            账户余额可用{" "}
            <span className="text-primary-600 font-[DIN]">
              {" "}
              ¥{balance?.canUseBalance}{" "}
            </span>
            ，请输入验证码进行支付
          </div>
          <Form.Item name="verCode" noStyle rules={[{ required: true }]}>
            <Input
              suffix={
                <Button type="link" onClick={getCode} size="small">
                  {countDown > 0
                    ? `${countDown}s`
                    : hadSent
                    ? "重新获取"
                    : "获取验证码"}
                </Button>
              }
              className="mb-2 pr-0"
              size="middle"
            />
          </Form.Item>
          <div className="text-text-secondary text-sm mb-9">
            {phoneOrEmail?.phone
              ? `验证码将发送至手机号 ${phoneOrEmail.phone}`
              : `验证码将发送至邮箱 ${phoneOrEmail?.email}`}
          </div>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldsValue }) => {
              const { verCode } = getFieldsValue();
              return (
                <Button
                  className="mb-3"
                  block
                  type="primary"
                  htmlType="submit"
                  disabled={!verCode}
                  size="large"
                >
                  确认支付 ¥{orderInfo.amount}
                </Button>
              );
            }}
          </Form.Item>
        </Form>
      </Modal>
      <PaySuccessModal
        open={successModalOpen}
        onClose={() => {
          setSuccessModalOpen(false);
          onSuccess();
        }}
      />
    </>
  );
}
